<template>
  <div class="redesign-dashboard">
    <!-- Header Section -->
    <div class="dashboard-header">
      <div class="header-info">
        <h1>Tableau de bord</h1>
        <div class="yellow-accent"></div>
        <p class="header-subtitle">Vue d’ensemble des garanties : nombre et montant par type.</p>
      </div>
    </div>

    <!-- Stats Summary Cards -->
    <div class="row stats-summary-row">
      <!-- Total Column -->
      <div class="col-12 col-lg-4 col-xl-3 mb-4">
        <div class="stats-card total-main h-100">
          <div class="card-icon">
            <font-awesome-icon prefix="fas" icon="chart-line" />
          </div>
          <div class="card-content">
            <span class="card-label">Totale</span>
            <h2 class="card-value">{{ totalPriceGaranties | formatEURWithoutSymbol | formatSeparateComma }} €</h2>
            <span class="card-count">{{ totalGaranties }} Garanties au total</span>
          </div>
        </div>
      </div>

      <!-- Mini Stats Column -->
      <div class="col-12 col-lg-8 col-xl-9">
        <div class="row h-100">
          <div v-for="(garanty, index) in garanties" :key="index" class="col-12 col-sm-6 col-md-4 col-xl-3 mb-4">
            <div class="stats-card mini-stats h-100" @click="goToGarantie(garanty.url)">
              <div class="card-accent" :style="{ backgroundColor: garanty.color }"></div>
              <div class="card-icon">
                <font-awesome-icon prefix="fas" :icon="garanty.icon" v-if="garanty.icon" />
                <span class="card-qty">{{ garanty.qty }}</span>
              </div>
              <div class="card-content">
                <span class="card-label">{{ garanty.title }}</span>
                <h3 class="card-value-small">
                  {{ getFullPrice(garanty) | formatEURWithoutSymbol | formatSeparateComma }} €
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-4">
      <!-- Chart Section -->
      <div class="col-12 col-lg-5 mb-4">
        <div class="glass-card chart-card">
          <div class="card-header">
            <h3>Vos statistiques</h3>
          </div>
          <div class="card-body">
            <div class="dougnhut-chart-wrapper">
              <div class="doughnut-center-info">
                <span class="total-label">Totale</span>
                <span class="total-value">{{ totalPriceGaranties | formatEURWithoutSymbol | formatSeparateComma }}
                  €</span>
              </div>
              <canvas id="account-quotes-doughnut-chart" width="400" height="400"></canvas>
            </div>


          </div>
        </div>
      </div>

      <!-- Recent Warranties Table -->
      <div class="col-12 col-lg-7 mb-4">
        <div class="glass-card table-card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h3>Garanties souscrites <span class="header-tag">(7 derniers jours)</span></h3>
          </div>
          <div class="card-body p-0">
            <template v-if="isBusy">
              <div class="table-loader">
                <Preloader />
              </div>
            </template>
            <template v-else>
              <div class="v2-dashboard-table-container">
                <table class="v2-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Nom</th>
                      <th>Garantie souscrite</th>
                      <th>{{ isGarden() ? 'Primes HT' : 'Prime' }}</th>
                      <th>Statut</th>
                      <th class="text-center">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="garanty in garantiesSouscrites" :key="garanty.id">
                      <td>{{ parseDate(garanty) }}</td>
                      <td class="font-weight-bold">{{ parseFullName(garanty) }}</td>
                      <td>
                        <span class="v2-badge v2-badge-blue">{{ parseTypeGaranty(garanty) }}</span>
                      </td>
                      <td class="v2-price">{{ parsePrice(garanty) | formatSeparateComma }} €</td>
                      <td>
                        <span class="v2-badge" :class="getBadgeClass(garanty)">
                          {{ parseStatusGaranty(garanty) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <button class="v2-btn-pdf" title="Télécharger PDF" @click="show_pdf(garanty.id)">
                          <font-awesome-icon prefix="fas" icon="file-pdf" />
                        </button>
                      </td>
                    </tr>
                    <tr v-if="garantiesSouscrites.length === 0">
                      <td colspan="6" class="text-center py-5 no-data">
                        Aucune garantie souscrite ces 7 derniers jours.
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Pagination Controls -->
                <div v-if="lastPage > 1" class="v2-pagination">
                  <button class="v2-page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
                    <font-awesome-icon prefix="fas" icon="chevron-left" />
                  </button>
                  <span class="v2-page-info">Page {{ currentPage }} sur {{ lastPage }}</span>
                  <button class="v2-page-btn" :disabled="currentPage === lastPage" @click="changePage(currentPage + 1)">
                    <font-awesome-icon prefix="fas" icon="chevron-right" />
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/scss/styleV2.scss";
</style>

<script>
import $ from 'jquery';
import Chart from 'chart.js';
import Preloader from '@/components/Preloader'
import pdfPreviewMixin from '../mixins/pdfPreviewMixin';
import RepresentativeGuemasPanel from "./RepresentativeGuemasPanel";
import RepresentativeMutuaidePanel from "./RepresentativeMutuaidePanel";
import RepresentativeDentalPanel from "./RepresentativeDentalPanel";

export default {
  name: 'TestMechanicPanel',
  components: { RepresentativeGuemasPanel, RepresentativeMutuaidePanel, RepresentativeDentalPanel, Preloader },
  mixins: [pdfPreviewMixin],
  data() {
    return {
      doughnutChart: null,
      isBusy: true,
      garantiesSouscrites: [],
      currentPage: 1,
      lastPage: 1,
      total: 0,
      perPage: 7
    }
  },
  computed: {
    mechanic_id() {
      return this.$store.state.userInfo.mechanic ? this.$store.state.userInfo.mechanic.id : '';
    },
    representative_id() {
      return this.$store.state.userInfo.representative ? this.$store.state.userInfo.representative.id : '';
    },
    is_GUEMAS_representative() {
      return this.$store.state.userInfo.representative ? (this.$store.state.userInfo.representative.id == 12) : false;
    },
    is_MUTUAIDE_representative() {
      return this.$store.state.userInfo.representative ? (this.$store.state.userInfo.representative.owner_type == 3) : false;
    },
    isClientMonthlyPayment() {
      return this.$store.getters.isClientMonthlyPayment;
    },
    totalGaranties() {
      let totalBalance = 0;
      for (let i in this.garanties) {
        totalBalance += +this.garanties[i].qty;
      }
      return totalBalance;
    },
    totalPriceGaranties() {
      let totalBalance = 0;
      for (let i in this.garanties) {
        totalBalance += +this.garanties[i].price;
      }
      return totalBalance;
    },
    garanties() {
      //MUTUAIDE
      if (this.isMutuaide()) {
        return [
          {
            title: 'Assistance Autocars',
            qty: this.getCountGaranties('autocarQty'),
            price: this.getPriceGaranties('autocarPrice'),
            color: '#008cc9'
          },
          {
            title: 'Assistance Poids Lourds',
            qty: this.getCountGaranties('poidslourdsQty'),
            price: this.getPriceGaranties('poidslourdsPrice'),
            color: '#ff0000'
          },
          {
            title: 'Assistance Taxi/Ambulances/Corbillards',
            qty: this.getCountGaranties('vslQty'),
            price: this.getPriceGaranties('vslPrice'),
            color: '#ffb510'
          },
          {
            title: 'Assistance VU - VL',
            qty: this.getCountGaranties('vuvlQty'),
            price: this.getPriceGaranties('vuvlPrice'),
            color: '#004767'
          },
          {
            title: 'Assistance VUL - 4097',
            qty: this.getCountGaranties('vul4097Qty'),
            price: this.getPriceGaranties('vul4097Price'),
            color: '#f69638'
          },
        ];
      }
      //GARDEN
      else if (this.isGarden()) {
        return [
          {
            title: 'Garantie Tondeuse, Robot',
            // qty: this.getCountGaranties('camping-carOldQty'),
            qty: 50,
            // price: this.getPriceGaranties('camping-carOldPrice'),
            price: 3465.00,
            color: '#008cc9'
          },
          {
            title: 'Garantie Tronçonneuse',
            // qty: this.getCountGaranties('camping-carNewQty'),
            qty: 10,
            // price: this.getPriceGaranties('camping-carNewPrice'),
            price: 264.00,
            color: '#ff0000'
          },
          {
            title: 'Garantie Taille-Haie',
            // qty: this.getCountGaranties('insuranceQty'),
            qty: 30,
            // price: this.getPriceGaranties('insurancePrice'),
            price: 459.00,
            color: '#ffb510'
          },
          {
            title: 'Garantie Débroussailleuse',
            // qty: this.getCountGaranties('insuranceQty'),
            qty: 20,
            // price: this.getPriceGaranties('insurancePrice'),
            price: 310.00,
            color: '#ffb510'
          },
          {
            title: 'Garantie Elagueuse',
            // qty: this.getCountGaranties('insuranceQty'),
            qty: 18,
            // price: this.getPriceGaranties('insurancePrice'),
            price: 220.00,
            color: '#ffb510'
          },
          {
            title: 'Garantie Autres',
            // qty: this.getCountGaranties('insuranceQty'),
            qty: 70,
            // price: this.getPriceGaranties('insurancePrice'),
            price: 1750.00,
            color: '#ffb510'
          },
        ];
      }
      //DEALER
      else if (this.isDealer()) {
        return [
          {
            title: 'Garantie camping-car occasion',
            qty: this.getCountGaranties('camping-carOldQty'),
            price: this.getPriceGaranties('camping-carOldPrice'),
            color: '#008cc9'
          },
          {
            title: 'Garantie camping car neuf',
            qty: this.getCountGaranties('camping-carNewQty'),
            price: this.getPriceGaranties('camping-carNewPrice'),
            color: '#ff0000'
          },
          {
            title: 'MASTERS Assurance',
            qty: this.getCountGaranties('insuranceQty'),
            price: this.getPriceGaranties('insurancePrice'),
            color: '#ffb510'
          },
        ];
      }
      //MECHANIC
      else {
        let returnArr = [
          {
            title: 'Vp - VU - 4x4',
            icon: 'car',
            qty: this.getCountGaranties('autoQty'),
            price: this.getPriceGaranties('autoPrice'),
            color: '#008cc9',
            url: '/garantie-auto-v2'
          },
          {
            title: 'Camping-Car',
            icon: 'truck-pickup',
            qty: this.getCountGaranties('camping-carQty'),
            price: this.getPriceGaranties('camping-carPrice'),
            color: '#ffc500',
            url: '/garantie-camping-car-v2'
          },
          {
            title: 'Moto',
            icon: 'motorcycle',
            qty: this.getCountGaranties('motoQty'),
            price: this.getPriceGaranties('motoPrice'),
            color: '#ff0000',
            url: '/garantie-moto-v2'
          },
        ];
        if (this.isClientMonthlyPayment) {
          returnArr.push({
            title: 'Auto Mensualisée',
            icon: 'car',
            qty: this.getCountGaranties('autoMonthlyQty'),
            price: this.getPriceGaranties('autoMonthlyPrice'),
            color: '#28a745',
            url: '/garantie-auto-mensualisee-v2'
          });
        }
        const speciality = this.isSpecialite();

        const hiddenItems = {
          'Auto': ['Camping-Car', 'Moto'],
          'Moto': ['Camping-Car', 'Vp - VU - 4x4', 'Auto Mensualisée'],
          'Camping-Car': ['Vp - VU - 4x4', 'Auto Mensualisée', 'Moto']
        };


        const data = returnArr.filter(
          item => !(hiddenItems[speciality] || []).includes(item.title)
        );

        return data;
      }
    },
    isDentalRepresentative() {
      return this.$store.getters.isRepresentative && (this.$store.getters.getRepresentative.id == 64 || this.$store.getters.getRepresentative.id == 65);
    }
  },
  methods: {
    goToGarantie(url) {
      this.$router.push(url);
    },
    isDealer() {
      return this.$store.getters.isDealer;
    },
    isSpecialite() {
      return this.$store.getters.isSpecialite;
    },
    isBroker() {
      return this.$store.getters.isBroker;
    },
    isMutuaide() {
      return this.$store.getters.isMutuaide;
    },
    isGarden() {
      return this.$store.getters.isGarden;
    },
    isRepresentative() {
      return this.$store.getters.isRepresentative;
    },
    getBadgeClass(garanty) {
      if (!garanty || !garanty.status) return 'v2-badge-gray';
      const status = garanty.status.toLowerCase();
      if (status === 'active' || status === 'valide') return 'v2-badge-green';
      if (status === 'inactive' || status === 'annulée') return 'v2-badge-red';
      if (status === 'expired' || status === 'echue') return 'v2-badge-gray';
      if (status === 'credited') return 'v2-badge-red';
      return 'v2-badge-blue';
    },
    changePage(page) {
      this.currentPage = page;
      if (this.representative_id) {
        this.getGarantiesRepresentativeSouscrites();
      } else {
        this.getGarantiesSouscrites();
      }
    },
    parseDate(garanty) {
      if (garanty && garanty.created_at) {
        if (garanty.datesParse && garanty.datesParse.day_created) {
          let day = garanty.datesParse.day_created,
            month = garanty.datesParse.month_created,
            year = garanty.datesParse.year_created;
          return day + "/" + month + '/' + year;
        }
        return this.moment(garanty.created_at).format('DD/MM/YYYY');
      }
      return ' - ';
    },
    parsePrice(garanty) {
      if (garanty.is_monthly === 1) {
        let monthly = garanty.monthly_price;
        if (garanty.status == 'Credited')
          return (monthly) ? '-' + this.roundEURWithoutSymbol(monthly) : ' - ';
        else
          return (monthly) ? this.roundEURWithoutSymbol(monthly) : ' - ';
      }
      let price = this.getFullPrice(garanty);
      if (garanty.status == 'Credited')
        return (price > 0) ? '-' + this.roundEURWithoutSymbol(price) : ' - '
      else
        return (price > 0) ? this.roundEURWithoutSymbol(price) : ' - '
    },
    getFullPrice(garanty) {
      let price = garanty.price || 0;
      let assistance_price = garanty.assistance_price || 0;
      return price + assistance_price;
    },
    parseFullName(garanty) {
      if (this.isRepresentative()) {
        return (garanty && garanty.mechanic && garanty.mechanic.garage) ? garanty.mechanic.garage : ' - ';
      }
      else {
        if (garanty && garanty.client) {
          const first = garanty.client.first_name || '';
          const last = garanty.client.last_name || '';
          const name = (first + ' ' + last).trim();
          return name || ' - ';
        }
        return ' - ';
      }
    },
    parseTypeGaranty(garanty) {
      if (this.isGarden()) {
        if (garanty && garanty.category) {
          switch (+garanty.category) {
            case 1: return 'Tondeuse, Robot';
            case 2: return 'Tronçonneuse';
            case 3: return 'Taille-Haie';
            case 4: return 'Débrousailleuse';
            case 5: return 'Elagueuse';
            case 6: return 'Autres';
          }
        }
      } else {
        if (garanty.garantie_name) return garanty.garantie_name;
        else if (garanty && garanty.category) {
          switch (+garanty.category) {
            case 2: return 'Auto';
            case 3: return 'Moto';
            case 1: return this.parseTypeGarantyForDealers(garanty);
            case 4: return 'Auto Mensualisée';
          }
        }
      }
      return ' - ';
    },
    parseStatusGaranty(garanty) {
      if (garanty && garanty.status) {
        switch (garanty.status) {
          case 'Active': return 'Valide';
          case 'Inactive': return 'Annulée';
          case 'Expired': return 'Echue';
          case 'Credited': return 'Sans Effet';
        }
      }
      return '';
    },
    parseTypeGarantyForDealers(guaranty) {
      if (this.isDealer()) {
        return (guaranty.details.is_new.toLowerCase() === "oui") ? 'Garantie camping car neuf' : 'Garantie camping-car occasion';
      }
      return 'Camping car';
    },
    getGarantiesSouscrites() {
      if (this.mechanic_id) {
        this.isBusy = true;
        let url;
        if (this.isDealer())
          url = 'garantie/dealer-search';
        else if (this.isMutuaide())
          url = 'garantie/mutuaide-search';
        else
          url = 'garantie/search';

        let filterParams = {
          start_date: this.moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          end_date: this.moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          pdf: 1,
          sort: 'id|desc',
          per_page: this.perPage,
          page: this.currentPage,
          specialite: this.isSpecialite(),
        },
          data = {
            mechanic_id: this.mechanic_id,
            ...filterParams,
          };
        this.apiGet(url, data)
          .then(response => {
            this.isBusy = false;
            this.garantiesSouscrites = response.data.data;
            this.lastPage = response.data.last_page;
            this.total = response.data.total;
          })
          .catch(error => {
            this.isBusy = false;
          })
      }
    },
    getGarantiesRepresentativeSouscrites() {
      if (this.representative_id) {
        this.isBusy = true;
        let url = 'garantie/representative-search';
        let filterParams = {
          start_date: this.moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          end_date: this.moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          pdf: 1,
          sort: 'id|desc',
          per_page: this.perPage,
          page: this.currentPage
        },
          data = {
            representative_id: this.representative_id,
            ...filterParams,
          };
        this.apiGet(url, data)
          .then(response => {
            this.isBusy = false;
            this.garantiesSouscrites = response.data.data;
            this.lastPage = response.data.last_page;
            this.total = response.data.total;
          })
          .catch(error => {
            this.isBusy = false;
          })
      }
    },
    getCountGaranties(type) {
      return this.$store.state.quotes && this.$store.state.quotes[type] ? +this.$store.state.quotes[type] : 0;
    },
    getPriceGaranties(type) {
      return this.$store.state.quotes && this.$store.state.quotes[type] ? this.roundEURWithoutSymbol(this.$store.state.quotes[type]) : 0;
    },
    drawAccountQuotesDoughnutChart() {
      let doughnutChartContainer = $('#account-quotes-doughnut-chart');
      if (!doughnutChartContainer.length) return;

      let data = [],
        backgroundColor = [],
        labels = [];

      for (let i in this.garanties) {
        data.push(this.garanties[i].price);
        backgroundColor.push(this.garanties[i].color);
        labels.push(this.garanties[i].title);
      }

      if (this.doughnutChart) {
        this.doughnutChart.destroy();
      }

      this.doughnutChart = new Chart(doughnutChartContainer, {
        type: 'doughnut',
        data: {
          datasets: [{
            data,
            backgroundColor,
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 10
          }],
          labels
        },
        options: {
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart'
          },
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 55,

          legend: {
            display: true,
            position: 'bottom',
            align: 'start',
            fullWidth: true,

            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 10,
              padding: 12,
              fontColor: '#334155'
            }
          },

          layout: {
            padding: {
              top: 10,
              bottom: 10
            }
          },

          tooltips: {
            enabled: true,
            mode: 'index',
            intersect: false,
            backgroundColor: '#1e293b',
            cornerRadius: 8,
            padding: 12,
            callbacks: {
              label: (tooltipItem, data) => {
                let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index],
                  label = data.labels[tooltipItem.index];
                return ' ' + label + ': ' + this.replaceDotCommaAmount(value) + ' €';
              }
            }
          }
        }
      });

    }
  },
  watch: {
    'garanties': function () {
      this.drawAccountQuotesDoughnutChart();
    },
    'mechanic_id': function () {
      this.getGarantiesSouscrites();
    },
    'representative_id': function () {
      this.getGarantiesRepresentativeSouscrites();
    }
  },
  mounted() {
    this.drawAccountQuotesDoughnutChart();
    if (this.representative_id) {
      this.getGarantiesRepresentativeSouscrites();
    }
    else {
      this.getGarantiesSouscrites();
    }
  }

}
</script>



// WEBPACK FOOTER //
// src/components/TestMechanicPanel.vue